import React, { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import { UserPlus, AlertTriangle } from 'lucide-react';
import { useCreateAppointmentMutation } from '../../api/appointmentsApi';
import { useCreatePatientMutation, useGetBookableProceduresQuery } from '../../api/patientsApi';
import { useListDoctorsQuery } from '../../api/usersApi';
import { applyApiErrorsToForm } from '../../utils/apiError';
import ApiErrorBanner from '../../components/ApiErrorBanner';
import PatientCombobox from '../../components/PatientCombobox';
import { doctorDisplayName } from './doctorColors';
import { findConflict } from './appointmentStatus';

const TABS = [
  { key: 'appointment', label: 'Appointment' },
  { key: 'reminder',    label: 'Reminder' },
  { key: 'block',       label: 'Block Calendar' },
];

function toInputDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function toInputTime(d) {
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}
function combineDateTime(dateStr, timeStr) {
  const [hh, mm] = (timeStr || '00:00').split(':');
  const d = new Date(dateStr);
  d.setHours(Number(hh) || 0, Number(mm) || 0, 0, 0);
  return d;
}

export default function AppointmentFormModal({
  initialDate,
  initialDoctorId,
  initialDurationMinutes,
  defaultTab,
  allAppointments = [],
  onClose,
  onCreated,
}) {
  const [tab, setTab] = useState(
    TABS.some((t) => t.key === defaultTab) ? defaultTab : 'appointment'
  );
  const [selectedPatient, setSelectedPatient] = useState(null); // chosen from combobox
  const [showNewPatientFields, setShowNewPatientFields] = useState(false);

  const [createPatient] = useCreatePatientMutation();
  const [createAppointment] = useCreateAppointmentMutation();

  const { data: doctors = [] } = useListDoctorsQuery();

  const defaults = useMemo(() => {
    const baseDate = initialDate ? new Date(initialDate) : new Date();
    return {
      // New-patient fields (only used when no patient is selected)
      newPatientName: '',
      newPatientPhone: '',
      // Appointment fields
      doctorId: initialDoctorId ?? '',
      treatmentTypeId: '',
      scheduledDate: toInputDate(baseDate),
      scheduledTime: toInputTime(baseDate),
      durationMinutes: initialDurationMinutes ?? 60,
      notes: '',
      // reminder tab
      reminderTitle: '',
      reminderDate: toInputDate(baseDate),
      reminderTime: toInputTime(baseDate),
      reminderBody: '',
      // block tab
      blockReason: '',
      blockFromDate: toInputDate(baseDate),
      blockFromTime: toInputTime(baseDate),
      blockToDate: toInputDate(baseDate),
      blockToTime: toInputTime(new Date(baseDate.getTime() + 60 * 60 * 1000)),
    };
  }, [initialDate, initialDoctorId, initialDurationMinutes]);

  const { register, handleSubmit, reset, setValue, setError, control, formState: { errors, isSubmitting } } =
    useForm({ defaultValues: defaults });
  const [serverError, setServerError] = useState(null);

  useEffect(() => { reset(defaults); }, [defaults, reset]);

  // Procedures this patient may currently be booked for: everything on their latest bill (bookable
  // even at a negative balance) plus older purchases with sessions left. A service that was never
  // billed simply does not appear — unlocking it requires a new bill, not a UI override.
  // A brand-new patient has no bill yet, so the dropdown is empty and the field stays optional.
  const { data: bookableProcedures = [], isFetching: proceduresLoading } = useGetBookableProceduresQuery(
    selectedPatient?.id,
    { skip: !selectedPatient?.id },
  );

  // Live-watch the time/doctor/duration fields so we can detect conflicts as the user types.
  const watchedDoctor   = useWatch({ control, name: 'doctorId' });
  const watchedDate     = useWatch({ control, name: 'scheduledDate' });
  const watchedTime     = useWatch({ control, name: 'scheduledTime' });
  const watchedDuration = useWatch({ control, name: 'durationMinutes' });

  const conflict = useMemo(() => {
    if (tab !== 'appointment') return null;
    if (!watchedDoctor || !watchedDate || !watchedTime) return null;
    const scheduledAt = combineDateTime(watchedDate, watchedTime);
    return findConflict(
      {
        assignedUserId: watchedDoctor,
        scheduledAt,
        durationMinutes: parseInt(watchedDuration, 10) || 60,
      },
      allAppointments,
    );
  }, [tab, watchedDoctor, watchedDate, watchedTime, watchedDuration, allAppointments]);

  const handleCombChange = (patient) => {
    setSelectedPatient(patient);
    if (patient) setShowNewPatientFields(false);
  };

  const handleCreateNew = (initialName) => {
    setSelectedPatient(null);
    setShowNewPatientFields(true);
    setValue('newPatientName', initialName ?? '');
  };

  const submitAppointment = async (data) => {
    setServerError(null);
    if (!data.doctorId) {
      setServerError('Please pick a doctor.');
      return;
    }
    if (!data.scheduledDate || !data.scheduledTime) {
      setServerError('Please pick a date and time.');
      return;
    }
    if (!selectedPatient && !showNewPatientFields) {
      setServerError('Please search and select a patient — or click "Add new patient" if they are not in the system.');
      return;
    }
    try {
      // 1. Resolve or create patient
      let patientId = selectedPatient?.id;
      let resolvedCode = selectedPatient?.patientCode;
      if (!patientId) {
        const name = data.newPatientName?.trim();
        const phone = data.newPatientPhone?.trim();
        if (!name || !phone) {
          setServerError('New patient: full name and mobile number are required.');
          return;
        }
        const created = await createPatient({
          fullName: name,
          phoneNumber: phone,
          gender: 'Unspecified',
        }).unwrap();
        patientId = created.id;
        resolvedCode = created.patientCode;
        toast.success(`Created patient ${created.patientCode}`);
      }

      // 2. Create appointment
      const scheduledAt = combineDateTime(data.scheduledDate, data.scheduledTime);
      const payload = {
        patientId,
        leadId: null,
        assignedUserId: data.doctorId,
        type: 'Consultation',
        scheduledAt: scheduledAt.toISOString(),
        durationMinutes: parseInt(data.durationMinutes, 10) || 60,
        notes: data.notes?.trim() || null,
        // Optional: a patient with no bill yet (or a brand-new one) books without a procedure and
        // shows the generic "Session" label until it is attributed later.
        treatmentTypeId: data.treatmentTypeId || null,
      };
      const appt = await createAppointment(payload).unwrap();
      toast.success(`Appointment scheduled for ${resolvedCode}`);
      onCreated?.(appt);
      onClose();
    } catch (e) {
      const msg = applyApiErrorsToForm(setError, e);
      setServerError(msg);
      toast.error(msg);
    }
  };

  const submitReminder = (data) => {
    toast.info('Reminder feature is wired in next release. Captured: ' + (data.reminderTitle || 'untitled'));
    onClose();
  };

  const submitBlock = (data) => {
    toast.info(`Calendar block captured (${data.blockFromDate} → ${data.blockToDate}). Wiring next release.`);
    onClose();
  };

  return (
    <div className="cal-form-overlay" onClick={onClose}>
      <div className="cal-form" onClick={(e) => e.stopPropagation()}>
        <div className="cal-form-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={`cal-form-tab ${tab === t.key ? 'active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'appointment' && (
          <form onSubmit={handleSubmit(submitAppointment)}>
            <div className="cal-form-body">
              <ApiErrorBanner message={serverError} onDismiss={() => setServerError(null)} />

              {/* Patient search — type ID / phone / name, pick from dropdown */}
              <div className="form-group">
                <label className="form-label">
                  Patient *
                  <span style={{ fontSize: '0.74rem', fontWeight: 400, color: 'var(--text-muted)', marginLeft: 8 }}>
                    type ID (e.g. P2473), phone, or name
                  </span>
                </label>
                <PatientCombobox
                  value={selectedPatient}
                  onChange={handleCombChange}
                  onCreateNew={handleCreateNew}
                  placeholder="Search by patient ID, phone, or name…"
                  autoFocus
                />
                {!selectedPatient && !showNewPatientFields && (
                  <button
                    type="button"
                    onClick={() => { setShowNewPatientFields(true); setSelectedPatient(null); }}
                    style={{
                      marginTop: 8,
                      padding: '6px 12px',
                      fontSize: '0.82rem',
                      fontWeight: 600,
                      color: 'var(--green-700)',
                      background: 'transparent',
                      border: '1px dashed var(--green-300)',
                      borderRadius: 'var(--r-md)',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <UserPlus size={14} /> Add a new patient instead
                  </button>
                )}
              </div>

              {/* New patient block (only shows when no existing patient is picked) */}
              {!selectedPatient && showNewPatientFields && (
                <>
                  <div style={{
                    padding: '8px 12px',
                    background: 'var(--green-50)',
                    border: '1px solid var(--green-300)',
                    borderRadius: 'var(--r-md)',
                    fontSize: '0.82rem',
                    color: 'var(--green-800, #065f46)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                  }}>
                    <span><strong>New patient</strong> — a P-code will be assigned automatically on save.</span>
                    <button
                      type="button"
                      onClick={() => setShowNewPatientFields(false)}
                      style={{ background: 'transparent', color: 'var(--green-800, #065f46)', fontWeight: 700, cursor: 'pointer', fontSize: '0.78rem' }}
                    >
                      Cancel
                    </button>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Full Name *</label>
                      <input
                        className="form-control"
                        {...register('newPatientName', { required: showNewPatientFields ? 'Required' : false })}
                      />
                      {errors.newPatientName && <span className="form-field-error">{errors.newPatientName.message}</span>}
                    </div>
                    <div className="form-group">
                      <label className="form-label">Mobile Number *</label>
                      <input
                        className="form-control"
                        {...register('newPatientPhone', { required: showNewPatientFields ? 'Required' : false })}
                      />
                      {errors.newPatientPhone && <span className="form-field-error">{errors.newPatientPhone.message}</span>}
                    </div>
                  </div>
                </>
              )}

              <hr className="cal-form-divider" />

              <div className="form-group">
                <label className="form-label">Doctor *</label>
                <select className="form-control" {...register('doctorId', { required: 'Choose a doctor' })}>
                  <option value="">— Select a doctor —</option>
                  {doctors.map((d) => (
                    <option key={d.id} value={d.id}>{doctorDisplayName(d.fullName)}{d.specialization ? ` · ${d.specialization}` : ''}</option>
                  ))}
                </select>
                {errors.doctorId && <span className="form-field-error">{errors.doctorId.message}</span>}
              </div>

              {selectedPatient && (
                <div className="form-group">
                  <label className="form-label">Session type</label>
                  <select className="form-control" {...register('treatmentTypeId')} disabled={proceduresLoading}>
                    <option value="">
                      {proceduresLoading ? 'Loading…' : 'Session (not specified)'}
                    </option>
                    {bookableProcedures.map((p) => (
                      <option key={p.treatmentTypeId} value={p.treatmentTypeId}>
                        {p.name} — {p.remaining} left{p.onLatestBill ? '' : ' (earlier bill)'}
                      </option>
                    ))}
                  </select>
                  {!proceduresLoading && bookableProcedures.length === 0 && (
                    <span className="form-hint">
                      Nothing billed yet — bill a procedure to make it bookable.
                    </span>
                  )}
                  {bookableProcedures.some((p) => p.remaining < 0) && (
                    <span className="form-hint" style={{ color: 'var(--danger, #c0392b)' }}>
                      Some balances are negative. Booking is still allowed on the current bill.
                    </span>
                  )}
                </div>
              )}

              <label className="form-label">Scheduled On</label>
              <div className="cal-form-sched-row">
                <input className="form-control" type="date" {...register('scheduledDate', { required: true })} />
                <span className="joiner">at</span>
                <input className="form-control" type="time" {...register('scheduledTime', { required: true })} />
                <span className="joiner">for</span>
                <select className="form-control" {...register('durationMinutes', { valueAsNumber: true })}>
                  <option value={15}>15 min</option>
                  <option value={30}>30 min</option>
                  <option value={45}>45 min</option>
                  <option value={60}>1 hr 0 min</option>
                  <option value={90}>1 hr 30 min</option>
                  <option value={120}>2 hr 0 min</option>
                </select>
              </div>

              {conflict && (
                <div className="cal-conflict-banner cal-conflict-advisory">
                  <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                  <div style={{ flex: 1 }}>
                    <strong>Heads up:</strong>{' '}
                    Dr. {doctorDisplayName(doctors.find((d) => d.id === conflict.assignedUserId)?.fullName ?? 'Doctor').replace(/^Dr\.\s+/, '')}{' '}
                    already has <strong>{conflict.patientName ?? 'a patient'}</strong> around{' '}
                    <strong>{new Date(conflict.scheduledAt).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' })}</strong>{' '}
                    ({conflict.durationMinutes ?? 30} min). You can still book this slot.
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-control" rows={3} placeholder="Add notes (optional). Pricing is set when collecting payment." {...register('notes')} />
              </div>
            </div>

            <div className="cal-form-footer">
              <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
              <button type="submit" className="cal-form-save" disabled={isSubmitting}>
                {isSubmitting ? 'Saving…' : 'Confirm Appointment'}
              </button>
            </div>
          </form>
        )}

        {tab === 'reminder' && (
          <form onSubmit={handleSubmit(submitReminder)}>
            <div className="cal-form-body">
              <div className="form-group">
                <label className="form-label">Reminder Title *</label>
                <input className="form-control" placeholder="e.g., Call back about treatment plan" {...register('reminderTitle')} />
              </div>
              <label className="form-label">When</label>
              <div className="cal-form-sched-row" style={{ gridTemplateColumns: '1fr auto 1fr' }}>
                <input className="form-control" type="date" {...register('reminderDate')} />
                <span className="joiner">at</span>
                <input className="form-control" type="time" {...register('reminderTime')} />
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-control" rows={4} {...register('reminderBody')} />
              </div>
              <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 8 }}>
                Reminders show up on the dashboard and in the today panel at the scheduled time.
              </p>
            </div>
            <div className="cal-form-footer">
              <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
              <button type="submit" className="cal-form-save">Save Reminder</button>
            </div>
          </form>
        )}

        {tab === 'block' && (
          <form onSubmit={handleSubmit(submitBlock)}>
            <div className="cal-form-body">
              <div className="form-group">
                <label className="form-label">Doctor</label>
                <select className="form-control" {...register('doctorId')}>
                  <option value="">All doctors (clinic-wide block)</option>
                  {doctors.map((d) => <option key={d.id} value={d.id}>{d.fullName}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Reason</label>
                <input className="form-control" placeholder="e.g., Annual leave, Conference" {...register('blockReason')} />
              </div>
              <label className="form-label">From</label>
              <div className="cal-form-sched-row" style={{ gridTemplateColumns: '1fr auto 1fr' }}>
                <input className="form-control" type="date" {...register('blockFromDate')} />
                <span className="joiner">at</span>
                <input className="form-control" type="time" {...register('blockFromTime')} />
              </div>
              <label className="form-label">To</label>
              <div className="cal-form-sched-row" style={{ gridTemplateColumns: '1fr auto 1fr' }}>
                <input className="form-control" type="date" {...register('blockToDate')} />
                <span className="joiner">at</span>
                <input className="form-control" type="time" {...register('blockToTime')} />
              </div>
              <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 8 }}>
                Blocking the calendar prevents new appointments from being booked in this range.
              </p>
            </div>
            <div className="cal-form-footer">
              <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
              <button type="submit" className="cal-form-save">Block</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
